import './echarts-register';
import './style.css';
import './views/pop/pop-touch.css';
import 'vue-echarts/style.css';
import {
  appendAdminChildRoutes,
  AX_TIER_DEFAULT_COLOR_PRESET,
  coreRoutes,
  createAxApp,
  createAxResourceRoute,
  replaceAdminDashboardRoute,
  setAxDataGridDefaults,
  SystemDocumentTemplatesPage,
  type AxAppContext,
  type AxResourceRouteDefinition,
  type BrandingConfig,
} from '@axsolutionsdev/core-fe';
import { AgentDockButton, AgentPanel } from '@axsolutionsdev/core-fe';
import type { RouteRecordRaw } from 'vue-router';
import { mergeMesBranding } from './branding';
import { installMesMenuCtrlClickNewTab } from './composables/mes-menu-new-tab.js';
import { installDateRangeShortcutHighlight } from './utils/date-range-shortcut-highlight.js';
import { isMixingOrdersEnabled, setMesFeatureFlags } from './utils/mes-features.js';
import {
  getProcessDataStatusRouteDefs,
  getProcessTempAlertConfigs,
  resolveProcessDataStatusProfile,
} from './views/data-status/process-data-status.config.js';
import type { ProcessDataStatusProfileId } from './views/data-status/process-data-status.types.js';

/**
 * `crypto.randomUUID` 는 secure context(HTTPS·localhost)에서만 제공된다. dev 를 원격
 * IP(http://ip:port)로 열면 undefined 라 로컬 임시 id 생성(`crypto.randomUUID()`)이 TypeError 로
 * 터진다(예: 단위 관리 「행 추가」, BOM·수주·입출고 라인 추가). `getRandomValues` 기반 v4 UUID 로
 * 폴백한다. 운영은 HTTPS 라 네이티브 구현이 그대로 쓰이며, 이 폴백은 dev-원격 접속에서만 동작한다.
 */
function installRandomUuidPolyfill(): void {
  const c = globalThis.crypto as Crypto | undefined;
  if (!c || typeof c.getRandomValues !== 'function' || typeof c.randomUUID === 'function') return;
  const gen = (): string => {
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    b[6] = ((b[6] ?? 0) & 0x0f) | 0x40; // version 4
    b[8] = ((b[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
    const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  try {
    (c as { randomUUID: Crypto['randomUUID'] }).randomUUID = gen as Crypto['randomUUID'];
  } catch {
    (Crypto.prototype as { randomUUID: Crypto['randomUUID'] }).randomUUID =
      gen as Crypto['randomUUID'];
  }
}
installRandomUuidPolyfill();

const MesResourcePage = () => import('./views/MesResourcePage.vue');
const MesOperationalResourcePage = () => import('./views/MesOperationalResourcePage.vue');
const MesStatusPage = () => import('./views/MesStatusPage.vue');

const ItemsPage = () => import('./views/master/ItemsPage.vue');
const ProductionPlansPage = () => import('./views/production/ProductionPlansPage.vue');
const ProductionRecordsPage = () => import('./views/production/ProductionRecordsPage.vue');
const WorkOrdersPage = () => import('./views/production/WorkOrdersPage.vue');
const PurchaseOrdersPage = () => import('./views/sales/PurchaseOrdersPage.vue');
const SalesOrdersPage = () => import('./views/sales/SalesOrdersPage.vue');
const PartnerLedgerPage = () => import('./views/accounting/PartnerLedgerPage.vue');
const PaymentJournalPage = () => import('./views/accounting/PaymentJournalPage.vue');
const MonthlySummaryPage = () => import('./views/accounting/MonthlySummaryPage.vue');
const WarehousesPage = () => import('./views/master/WarehousesPage.vue');
const PartnersPage = () => import('./views/master/PartnersPage.vue');
const PartnerItemPricesPage = () => import('./views/master/PartnerItemPricesPage.vue');
const ProcessesPage = () => import('./views/master/ProcessesPage.vue');
const EquipmentsPage = () => import('./views/master/EquipmentsPage.vue');
const EquipmentHistoryPage = () => import('./views/master/EquipmentHistoryPage.vue');
const EquipmentPartsPage = () => import('./views/equipment/EquipmentPartsPage.vue');
const EquipmentHistoryStatusPage = () => import('./views/equipment/EquipmentHistoryStatusPage.vue');
const EquipmentSensorsPage = () => import('./views/master/EquipmentSensorsPage.vue');
const BomsPage = () => import('./views/master/BomsPage.vue');
const RoutingsPage = () => import('./views/master/RoutingsPage.vue');
// 업무문서 인쇄는 코어 문서 서식 엔진의 범용 페이지 1개로 수렴한다 (L.CORE.DOC.3).
const DocumentPrintPage = () => import('./reports/DocumentPrintPage.vue');
const MesPopEntryPage = () => import('./views/pop/MesPopEntryPage.vue');
const MesPopStockIoHubPage = () => import('./views/pop/MesPopStockIoHubPage.vue');
const MesPopDataMonitorPage = () => import('./views/pop/MesPopDataMonitorPage.vue');
const MesPopSpecimenTestPage = () => import('./views/pop/MesPopSpecimenTestPage.vue');
const MesDataStatusPlaceholderPage = () => import('./views/MesDataStatusPlaceholderPage.vue');
const MesProcessDataStatusPage = () =>
  import('./views/data-status/JayeonBadaProcessDataStatusPage.vue');
// 공정데이터 수기입력 — 전 테넌트 공용, 노출은 메뉴 시드로 게이트 (L.MES.GUPO.8 §4.2.6)
const MesProcessDataManualEntryPage = () =>
  import('./views/data-status/ProcessDataManualEntryPage.vue');
const MesProcessTempAlertPage = () => import('./views/data-status/ProcessTempAlertPage.vue');
const MesStorageTempTrendPage = () => import('./views/data-status/StorageTempTrendPage.vue');
const MesDashboardPage = () => import('./views/MesDashboardPage.vue');
const GoodsReceiptsPage = () => import('./views/inventory/GoodsReceiptsPage.vue');
const GoodsIssuesPage = () => import('./views/inventory/GoodsIssuesPage.vue');
const MaterialIssuesPage = () => import('./views/inventory/MaterialIssuesPage.vue');
const InventoryTransfersPage = () => import('./views/inventory/InventoryTransfersPage.vue');
const InventoryCountsPage = () => import('./views/inventory/InventoryCountsPage.vue');
const InventoryStockStatusPage = () => import('./views/inventory/InventoryStockStatusPage.vue');
const InventoryIoStatusPage = () => import('./views/inventory/InventoryIoStatusPage.vue');
const MaterialUsagePage = () => import('./views/inventory/MaterialUsagePage.vue');

// sHACCP 화면 (L.MES.HACCP.1)
const HaccpDashboardPage = () => import('./views/haccp/HaccpDashboardPage.vue');
const HygieneCheckLogDailyPage = () => import('./views/haccp/HygieneCheckLogPage.vue');
const HygieneCheckLogVisualPage = () => import('./views/haccp/HygieneCheckLogPage.vue');
const CcpPointsPage = () => import('./views/haccp/CcpPointsPage.vue');
// HACCP 일지 엔진 (AX 계열 이식, 설계 mes-haccp-engine-port.md)
const HaccpTemplateDesignerPage = () => import('./views/haccp/HaccpTemplateDesignerPage.vue');
const HaccpSheetListPage = () => import('./views/haccp/HaccpSheetListPage.vue');
const HaccpSheetPage = () => import('./views/haccp/HaccpSheetPage.vue');
const HaccpSheetPrintPage = () => import('./reports/HaccpSheetPrintPage.vue');
const CcpMonitoringPage = () => import('./views/haccp/CcpMonitoringPage.vue');
const HaccpMaterialLedgerPage = () => import('./views/haccp/HaccpMaterialLedgerPage.vue');
const HaccpProductLedgerPage = () => import('./views/haccp/HaccpProductLedgerPage.vue');
const HygieneCheckFormPage = () => import('./views/haccp/HygieneCheckFormPage.vue');
// 보건증관리 — jyf 라인 포팅 (L.MES.GUPO.8 §4.2.8)
const HealthCertPage = () => import('./views/haccp/HealthCertPage.vue');

type MesResourceRoute = AxResourceRouteDefinition;

/** A4 인쇄·PDF 전용 라우트 (AdminLayout·메뉴 없음) */
const mesReportPrintRoutes: RouteRecordRaw[] = [
  {
    // 코어 문서 서식 엔진 — 발주서·수주서·작업지시서·거래명세서 공용 (docType 파라미터)
    path: '/documents/:docType/:sourceId/print',
    name: 'documents.print',
    component: DocumentPrintPage,
    meta: {
      requiresAuth: true,
      permission: 'system:document:render',
    },
  },
  // 구 경로 — 북마크·열린 탭 보호용 리다이렉트 (한 라운드 유지 후 제거)
  {
    path: '/reports/purchase-order/:id/print',
    name: 'reports.purchase-order.print',
    redirect: (to) => ({
      name: 'documents.print',
      params: { docType: 'PURCHASE_ORDER', sourceId: String(to.params.id ?? '') },
      query: to.query,
    }),
  },
  {
    path: '/reports/sales-order/:id/print',
    name: 'reports.sales-order.print',
    redirect: (to) => ({
      name: 'documents.print',
      params: { docType: 'SALES_ORDER', sourceId: String(to.params.id ?? '') },
      query: to.query,
    }),
  },
  {
    path: '/reports/work-order/:id/print',
    name: 'reports.work-order.print',
    redirect: (to) => ({
      name: 'documents.print',
      params: { docType: 'WORK_ORDER', sourceId: String(to.params.id ?? '') },
      query: to.query,
    }),
  },
  {
    path: '/reports/haccp-sheet/:id/print',
    name: 'reports.haccp-sheet.print',
    component: HaccpSheetPrintPage,
    meta: {
      requiresAuth: true,
      permission: 'mes:haccp-sheet:read',
    },
  },
];

const mesPopBaseRoutes: RouteRecordRaw[] = [
  {
    path: '/pop/production-records',
    name: 'pop.production-records',
    component: MesPopEntryPage,
    props: { mode: 'production-records' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:production-record:create',
    },
  },
  {
    path: '/pop/material-issues',
    name: 'pop.material-issues',
    component: MesPopEntryPage,
    props: { mode: 'material-issues' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:material-issue:create',
    },
  },
  {
    path: '/pop/goods-issues',
    name: 'pop.goods-issues',
    component: MesPopEntryPage,
    props: { mode: 'goods-issues' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:goods-issue:create',
    },
  },
  {
    path: '/pop/goods-receipts',
    name: 'pop.goods-receipts',
    component: MesPopEntryPage,
    props: { mode: 'goods-receipts' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:goods-receipt:create',
    },
  },
  {
    path: '/pop/inventory-counts',
    name: 'pop.inventory-counts',
    component: MesPopEntryPage,
    props: { mode: 'inventory-counts' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:inventory-count:create',
    },
  },
  {
    path: '/pop/inventory-transfers',
    name: 'pop.inventory-transfers',
    component: MesPopEntryPage,
    props: { mode: 'inventory-transfers' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:inventory-transfer:create',
    },
  },
  {
    path: '/pop/stock-io-hub',
    name: 'pop.stock-io-hub',
    component: MesPopStockIoHubPage,
    meta: {
      requiresAuth: true,
      menuCode: 'mes.pop.stock-io-hub',
      permission: 'mes:pop-hub:read',
    },
  },
  {
    path: '/pop/data-monitor',
    name: 'pop.data-monitor',
    component: MesPopDataMonitorPage,
    meta: {
      requiresAuth: true,
      menuCode: 'mes.pop.data-monitor',
      permission: 'mes:data-monitor:read',
    },
  },
  {
    // 시편테스트 터치 기록 — HACCP 일지 엔진 재사용 (L.MES.GUPO.10 §5.6)
    path: '/pop/specimen-test',
    name: 'pop.specimen-test',
    component: MesPopSpecimenTestPage,
    meta: {
      requiresAuth: true,
      menuCode: 'mes.pop.specimen-test',
      permission: 'mes:haccp-prereq-log:write',
    },
  },
];

/**
 * Tier 2 MES 라우트를 AdminLayout 에 한 번만 등록한다 (HMR 중복 방지).
 *
 * Tier 3/4 FE(`@axsolutionsdev/mes-egm-fe` 등)는 본 모듈의 `createMesFeApp()`을 호출해
 * 동일 화면을 계승한다 — 소스 복제 없이 `@axsolutionsdev/mes-fe/bootstrap` 으로 공유.
 */
let mesFeRoutesInstalled = false;

/**
 * 수기입력 화면의 센서명→파라미터 키 매핑 — 프로필 gridParamLabels 를 뒤집어 만든다.
 * (센서 카탈로그에는 파라미터 키가 없어, 시드에서 명칭을 맞춰 둔 라벨로 잇는다.)
 */
function buildManualEntryParamKeyByLabel(
  profile: ReturnType<typeof resolveProcessDataStatusProfile>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const binding of Object.values(profile)) {
    for (const [key, label] of Object.entries(binding.screen.gridParamLabels)) {
      map[label] ??= key;
    }
  }
  return map;
}

function buildMesDataStatusRoutes(profileId: ProcessDataStatusProfileId): RouteRecordRaw[] {
  const profile = resolveProcessDataStatusProfile(profileId);
  const processRoutes: RouteRecordRaw[] = getProcessDataStatusRouteDefs(profileId).map((def) => {
    const binding = profile[def.routeKey];
    if (!binding) {
      throw new Error(
        `process-data-status profile '${profileId}' 에 routeKey '${def.routeKey}' 바인딩이 없습니다`,
      );
    }
    return {
      path: `mes/data-status/${def.path}`,
      name: def.name,
      component: MesProcessDataStatusPage,
      props: {
        processTypeCode: binding.processTypeCode,
        screen: binding.screen,
      },
      meta: {
        requiresAuth: true,
        menuCode: def.menuCode,
        permission: 'mes:data-status:read',
      },
    };
  });

  // 공정관리 온도알림 — kind 별 다른 UI: gauge(설비 게이지) / timeseries(일별 추이 그래프).
  const tempAlertRoutes: RouteRecordRaw[] = getProcessTempAlertConfigs(profileId).map((config) => ({
    path: `mes/data-status/${config.path}`,
    name: config.name,
    component: config.kind === 'timeseries' ? MesStorageTempTrendPage : MesProcessTempAlertPage,
    props: { config },
    meta: {
      requiresAuth: true,
      menuCode: config.menuCode,
      permission: 'mes:data-status:read',
    },
  }));

  return [
    ...processRoutes,
    ...tempAlertRoutes,
    // 공정데이터 수기입력 — 설비·센서 선택 → POST /mes/process-data(MANUAL).
    // 전 테넌트 공용 라우트, 노출은 메뉴 시드로 게이트 (L.MES.GUPO.8 §4.2.6).
    {
      path: 'mes/data-status/manual-entry',
      name: 'mes.data-status.manual-entry',
      component: MesProcessDataManualEntryPage,
      props: { paramKeyByLabel: buildManualEntryParamKeyByLabel(profile) },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.data-status.manual-entry',
        permission: 'mes:process-data:create',
      },
    },
    // Tier 2/3 일반 샘플 — 자연바다식품(Tier 4) 4개 공정 메뉴와 달리 시드 카탈로그에서 공통 노출.
    {
      path: 'mes/data-status/sample-process',
      name: 'mes.data-status.sample-process',
      component: MesDataStatusPlaceholderPage,
      props: {
        title: '공정현황(샘플)',
        hint: '공정 데이터(ProcessDataLog) 연동 후 이 화면에 표시됩니다.',
        permission: 'mes:data-status:read',
      },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.data-status.sample-process',
        permission: 'mes:data-status:read',
      },
    },
    {
      path: 'mes/data-status/sample-equipment',
      name: 'mes.data-status.sample-equipment',
      component: MesDataStatusPlaceholderPage,
      props: {
        title: '설비현황(샘플)',
        hint: '설비 가동·센서 이력 연동 후 이 화면에 표시됩니다.',
        permission: 'mes:data-status:read',
      },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.data-status.sample-equipment',
        permission: 'mes:data-status:read',
      },
    },
  ];
}

/**
 * 반죽지시 전용 POP — 구포국수 전용 기능이라 `mixingOrders` 플래그가 켜진 테넌트에만 등록한다
 * (설계 mes-mixing-orders-gupo-isolation.md P0). 포장지시 POP 은 반죽지시와 쌍으로만 쓰여
 * 같은 플래그를 따른다.
 */
const mesPopMixingRoutes: RouteRecordRaw[] = [
  {
    path: '/pop/mixing-orders',
    name: 'pop.mixing-orders',
    component: MesPopEntryPage,
    props: { mode: 'mixing-orders' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:production-record:create',
    },
  },
  {
    path: '/pop/packing-orders',
    name: 'pop.packing-orders',
    component: MesPopEntryPage,
    props: { mode: 'packing-orders' },
    meta: {
      requiresAuth: true,
      permission: 'mes:pop:production-record:create',
    },
  },
];

function mesPopRoutes(): RouteRecordRaw[] {
  return isMixingOrdersEnabled()
    ? [...mesPopBaseRoutes, ...mesPopMixingRoutes]
    : [...mesPopBaseRoutes];
}

function ensureMesFeRoutesInstalled(profileId: ProcessDataStatusProfileId = 'jayeon-bada'): void {
  if (mesFeRoutesInstalled) {
    return;
  }
  mesFeRoutesInstalled = true;

  const masterRoutes: RouteRecordRaw[] = [
    {
      path: 'mes/master/items',
      name: 'mes.master.items',
      component: ItemsPage,
      meta: { requiresAuth: true, menuCode: 'mes.master.items', permission: 'mes:item:read' },
    },
    {
      path: 'mes/master/boms',
      name: 'mes.master.boms',
      component: BomsPage,
      meta: { requiresAuth: true, menuCode: 'mes.master.boms', permission: 'mes:bom:read' },
    },
    {
      path: 'mes/master/warehouses',
      name: 'mes.master.warehouses',
      component: WarehousesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.warehouses',
        permission: 'mes:warehouse:read',
      },
    },
    {
      path: 'mes/master/partners',
      name: 'mes.master.partners',
      component: PartnersPage,
      meta: { requiresAuth: true, menuCode: 'mes.master.partners', permission: 'mes:partner:read' },
    },
    {
      path: 'mes/master/partner-item-prices',
      name: 'mes.master.partner-item-prices',
      component: PartnerItemPricesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.partner-item-prices',
        permission: 'mes:partner-item-price:read',
      },
    },
    {
      path: 'mes/master/processes',
      name: 'mes.master.processes',
      component: ProcessesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.processes',
        permission: 'mes:process:read',
      },
    },
    {
      path: 'mes/master/routings',
      name: 'mes.master.routings',
      component: RoutingsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.routings',
        permission: 'mes:process-routing:read',
      },
    },
    {
      path: 'mes/master/equipments',
      name: 'mes.master.equipments',
      component: EquipmentsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.equipments',
        permission: 'mes:equipment:read',
      },
    },
    {
      path: 'mes/master/equipment-history',
      name: 'mes.master.equipment-history',
      component: EquipmentHistoryPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.equipment-history',
        permission: 'mes:equipment-maintenance:read',
      },
    },
    // 설비관리 › 설비부품관리·설비이력현황 (구포 #55·#56, designs/mes-equipment-parts-and-history.md)
    {
      path: 'mes/equipment/parts',
      name: 'mes.equipment.parts',
      component: EquipmentPartsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.equipment.parts',
        permission: 'mes:equipment:read',
      },
    },
    {
      path: 'mes/equipment/history-status',
      name: 'mes.equipment.history-status',
      component: EquipmentHistoryStatusPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.equipment.history-status',
        permission: 'mes:equipment-maintenance:read',
      },
    },
    {
      path: 'mes/master/equipment-sensors',
      name: 'mes.master.equipment-sensors',
      component: EquipmentSensorsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.equipment-sensors',
        permission: 'mes:equipment-sensor:read',
      },
    },
    // 문서 서식 관리 — core 디자이너 화면 재사용, MES 메뉴 하이라이트용 alias 라우트.
    // 설계: docs/designs/mes-document-template-management.md §2 D2
    {
      path: 'mes/master/document-templates',
      name: 'mes.master.document-templates',
      component: SystemDocumentTemplatesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.master.document-templates',
        permission: 'system:document-template:read',
      },
    },
  ];

  const salesRoutes: (MesResourceRoute | RouteRecordRaw)[] = [
    {
      path: 'mes/sales/purchase-orders',
      name: 'mes.sales.purchase-orders',
      component: PurchaseOrdersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.sales.purchase-orders',
        permission: 'mes:purchase-order:read',
      },
    },
    {
      path: 'mes/sales/purchase-status',
      name: 'mes.sales.purchase-status',
      menuCode: 'mes.sales.purchase-status',
      permission: 'mes:purchase-order:read',
      title: '발주현황',
      endpoint: '/mes/purchase-orders',
    },
    {
      path: 'mes/sales/sales-orders',
      name: 'mes.sales.sales-orders',
      component: SalesOrdersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.sales.sales-orders',
        permission: 'mes:sales-order:read',
      },
    },
    {
      path: 'mes/sales/sales-status',
      name: 'mes.sales.sales-status',
      menuCode: 'mes.sales.sales-status',
      permission: 'mes:sales-order:read',
      title: '수주현황',
      endpoint: '/mes/sales-orders',
    },
  ];

  // 회계장부 — 설계 mes-sales-payment-ledger-and-order-simplify §4.5. 메뉴·권한은 테넌트 스크립트로만
  // 라이브 반영(gupo 전용 §4.6) — 메뉴 없는 테넌트는 라우트만 존재(권한 없음 → 403).
  const accountingRoutes: RouteRecordRaw[] = [
    {
      path: 'mes/accounting/partner-ledger',
      name: 'mes.accounting.partner-ledger',
      component: PartnerLedgerPage,
      props: { kind: 'sales' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.accounting.partner-ledger',
        permission: 'mes:partner-ledger:read',
      },
    },
    // 설계 §9 — 매입장부·입출금 내역·월별 요약 (권한은 원장 read 공유)
    {
      path: 'mes/accounting/purchase-partner-ledger',
      name: 'mes.accounting.purchase-partner-ledger',
      component: PartnerLedgerPage,
      props: { kind: 'purchase' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.accounting.purchase-partner-ledger',
        permission: 'mes:partner-ledger:read',
      },
    },
    {
      path: 'mes/accounting/payments',
      name: 'mes.accounting.payments',
      component: PaymentJournalPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.accounting.payments',
        permission: 'mes:partner-ledger:read',
      },
    },
    // 품목별 수불부 — 입출고현황의 구매입고·판매출고 한정 화면 (구포 #54)
    {
      path: 'mes/accounting/item-ledger',
      name: 'mes.accounting.item-ledger',
      component: InventoryIoStatusPage,
      props: { variant: 'trade' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.accounting.item-ledger',
        permission: 'mes:inventory-balance:read',
      },
    },
    {
      path: 'mes/accounting/monthly-summary',
      name: 'mes.accounting.monthly-summary',
      component: MonthlySummaryPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.accounting.monthly-summary',
        permission: 'mes:partner-ledger:read',
      },
    },
  ];

  const productionRoutes: (MesResourceRoute | RouteRecordRaw)[] = [
    {
      path: 'mes/production/plans',
      name: 'mes.production.plans',
      component: ProductionPlansPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.production.plans',
        permission: 'mes:production-plan:read',
      },
    },
    {
      path: 'mes/production/work-orders',
      name: 'mes.production.work-orders',
      component: WorkOrdersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.production.work-orders',
        permission: 'mes:work-order:read',
      },
    },
    // 반죽지시/포장지시 전용 화면 — 같은 컴포넌트에 kind 고정 (설계 mes-pop-mixing-packing-screens §4.3).
    // 구포국수 전용 기능이라 `mixingOrders` 플래그가 켜진 테넌트에만 등록한다
    // (설계 mes-mixing-orders-gupo-isolation.md P0).
    ...(isMixingOrdersEnabled()
      ? ([
          {
            path: 'mes/production/work-orders/mixing',
            name: 'mes.production.work-orders-mixing',
            component: WorkOrdersPage,
            props: { kind: 'MIXING' },
            meta: {
              requiresAuth: true,
              menuCode: 'mes.production.work-orders-mixing',
              permission: 'mes:work-order:read',
            },
          },
          {
            path: 'mes/production/work-orders/packing',
            name: 'mes.production.work-orders-packing',
            component: WorkOrdersPage,
            props: { kind: 'PACKING' },
            meta: {
              requiresAuth: true,
              menuCode: 'mes.production.work-orders-packing',
              permission: 'mes:work-order:read',
            },
          },
        ] as RouteRecordRaw[])
      : []),
    {
      path: 'mes/production/records',
      name: 'mes.production.records',
      component: ProductionRecordsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.production.records',
        permission: 'mes:production-record:read',
      },
    },
    {
      path: 'mes/production/wo-record-status',
      name: 'mes.production.wo-record-status',
      menuCode: 'mes.production.wo-record-status',
      permission: 'mes:production-record:read',
      title: '작업지시별생산현황',
      endpoint: '/mes/production-records',
    },
    {
      path: 'mes/production/daily-status',
      name: 'mes.production.daily-status',
      menuCode: 'mes.production.daily-status',
      permission: 'mes:production-record:read',
      title: '일별생산현황',
      endpoint: '/mes/production-records',
    },
    {
      path: 'mes/production/record-status',
      name: 'mes.production.record-status',
      menuCode: 'mes.production.record-status',
      permission: 'mes:production-record:read',
      title: '생산실적현황',
      endpoint: '/mes/production-records',
    },
    {
      path: 'mes/production/material-issue-status',
      name: 'mes.production.material-issue-status',
      menuCode: 'mes.production.material-issue-status',
      permission: 'mes:material-issue:read',
      title: '생산자재불출현황',
      endpoint: '/mes/material-issues',
    },
    {
      path: 'mes/production/lot-tracking',
      name: 'mes.production.lot-tracking',
      menuCode: 'mes.production.lot-tracking',
      permission: 'mes:lot:read',
      title: 'Lot 추적',
      endpoint: '/mes/lots',
    },
  ];

  const qualityRoutes: MesResourceRoute[] = [
    {
      path: 'mes/quality/criteria',
      name: 'mes.quality.criteria',
      menuCode: 'mes.quality.criteria',
      permission: 'mes:quality-checklist:read',
      title: '검사 기준 관리',
      endpoint: '/mes/quality-checklists',
    },
    {
      path: 'mes/quality/incoming',
      name: 'mes.quality.incoming',
      menuCode: 'mes.quality.incoming',
      permission: 'mes:quality-inspection:read',
      title: '수입검사 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/in-process',
      name: 'mes.quality.in-process',
      menuCode: 'mes.quality.in-process',
      permission: 'mes:quality-inspection:read',
      title: '공정 검사 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/final',
      name: 'mes.quality.final',
      menuCode: 'mes.quality.final',
      permission: 'mes:quality-inspection:read',
      title: '최종 검사 관리',
      endpoint: '/mes/quality-inspections',
    },
    {
      path: 'mes/quality/status',
      name: 'mes.quality.status',
      menuCode: 'mes.quality.status',
      permission: 'mes:quality-inspection:read',
      title: '검사 현황',
      endpoint: '/mes/quality-inspections',
    },
  ];

  const inventoryRoutes: (MesResourceRoute | RouteRecordRaw)[] = [
    {
      path: 'mes/inventory/goods-receipts',
      name: 'mes.inventory.goods-receipts',
      component: GoodsReceiptsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.goods-receipts',
        permission: 'mes:goods-receipt:read',
      },
    },
    {
      path: 'mes/inventory/goods-issues',
      name: 'mes.inventory.goods-issues',
      component: GoodsIssuesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.goods-issues',
        permission: 'mes:goods-issue:read',
      },
    },
    {
      path: 'mes/inventory/material-issues',
      name: 'mes.inventory.material-issues',
      component: MaterialIssuesPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.material-issues',
        permission: 'mes:material-issue:read',
      },
    },
    {
      path: 'mes/inventory/transfers',
      name: 'mes.inventory.transfers',
      component: InventoryTransfersPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.transfers',
        permission: 'mes:inventory-transfer:read',
      },
    },
    {
      path: 'mes/inventory/counts',
      name: 'mes.inventory.counts',
      component: InventoryCountsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.counts',
        permission: 'mes:inventory-count:read',
      },
    },
    // R.MES.INV.2 — 도식화 포함한 dedicated 페이지(차트+그리드). 더 이상 generic
    // MesStatusPage 경유하지 않으므로 isStatusRoute 목록에서도 빼야 한다.
    {
      path: 'mes/inventory/item-stock-status',
      name: 'mes.inventory.item-stock-status',
      component: InventoryStockStatusPage,
      props: { groupBy: 'item' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.item-stock-status',
        permission: 'mes:inventory-balance:read',
      },
    },
    // 입출고현황 — 품목 기준 재고 입출고 명세 (설계 docs/designs/mes-inventory-io-status.md)
    {
      path: 'mes/inventory/io-status',
      name: 'mes.inventory.io-status',
      component: InventoryIoStatusPage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.io-status',
        permission: 'mes:inventory-balance:read',
      },
    },
    // 자재출고내역 — BOM 투입 자재를 사용일별로 묶어 본다 (구포 #66)
    {
      path: 'mes/inventory/material-usage',
      name: 'mes.inventory.material-usage',
      component: MaterialUsagePage,
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.material-usage',
        permission: 'mes:inventory-balance:read',
      },
    },
    {
      path: 'mes/inventory/warehouse-stock-status',
      name: 'mes.inventory.warehouse-stock-status',
      component: InventoryStockStatusPage,
      props: { groupBy: 'warehouse' },
      meta: {
        requiresAuth: true,
        menuCode: 'mes.inventory.warehouse-stock-status',
        permission: 'mes:inventory-balance:read',
      },
    },
  ];

  function toResourceRoute(r: MesResourceRoute): RouteRecordRaw {
    const route = createAxResourceRoute(r, {
      component: isStatusRoute(r.menuCode)
        ? MesStatusPage
        : isOperationalEndpoint(r.endpoint)
          ? MesOperationalResourcePage
          : MesResourcePage,
    });
    const inspectionType =
      r.menuCode === 'mes.quality.incoming'
        ? 'INCOMING'
        : r.menuCode === 'mes.quality.in-process'
          ? 'IN_PROCESS'
          : r.menuCode === 'mes.quality.final'
            ? 'FINAL'
            : undefined;
    // vue-router의 RouteRecordRaw 유니온은 object spread 시 exactOptionalPropertyTypes와
    // 충돌하므로(선택 속성이 undefined로 넓어짐) 병합 결과를 명시적으로 좁혀준다.
    return inspectionType
      ? ({
          ...route,
          props: { ...(typeof route.props === 'object' ? route.props : {}), inspectionType },
        } as RouteRecordRaw)
      : route;
  }

  function isOperationalEndpoint(endpoint: string): boolean {
    return [
      '/mes/production-plans',
      '/mes/work-orders',
      '/mes/production-records',
      '/mes/quality-checklists',
      '/mes/quality-inspections',
      '/mes/quality-results',
    ].includes(endpoint);
  }

  function isStatusRoute(menuCode: string): boolean {
    return [
      'mes.sales.purchase-status',
      'mes.sales.sales-status',
      'mes.production.wo-record-status',
      'mes.production.daily-status',
      'mes.production.record-status',
      'mes.production.material-issue-status',
      'mes.production.lot-tracking',
      'mes.quality.dashboard',
      // R.MES.INV.2 — item/warehouse stock 은 dedicated InventoryStockStatusPage 로 이전됨.
    ].includes(menuCode);
  }

  // sHACCP 라우트 (L.MES.HACCP.1 §5.8)
  const haccpRoutes: RouteRecordRaw[] = [
    // 해썹 현황 대시보드
    {
      path: 'mes/haccp/dashboard',
      name: 'haccp.dashboard',
      component: HaccpDashboardPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.dashboard',
        permission: 'mes:haccp-dashboard:read',
      },
    },
    // 선행요건 — 일반위생·공정점검표(일별)
    {
      path: 'mes/haccp/prerequisite/daily-hygiene',
      name: 'haccp.prerequisite.daily-hygiene',
      component: HygieneCheckLogDailyPage,
      props: { formCategory: 'DAILY' },
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.daily-hygiene',
        permission: 'mes:haccp-prereq-log:read',
      },
    },
    // 선행요건 — 육안검사일지
    {
      path: 'mes/haccp/prerequisite/visual-inspection',
      name: 'haccp.prerequisite.visual-inspection',
      component: HygieneCheckLogVisualPage,
      props: { formCategory: 'WEEKLY' },
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.visual-inspection',
        permission: 'mes:haccp-prereq-log:read',
      },
    },
    // CCP 일지 — CCP 별 동적 메뉴 → 일지 엔진 목록(달력·Drawer). ccpId 의 한계기준에 바인딩된 서식만 (설계 mes-haccp-engine-port §4.2)
    {
      path: 'mes/haccp/ccp-log/:ccpId',
      name: 'haccp.ccp-log',
      component: HaccpSheetListPage,
      props: (route) => ({
        // CCP 바인딩 서식은 카테고리 무관(냉장·냉동은 선행요건 등) — ccpId 필터만 적용
        category: 'CCP,PREREQUISITE,EVENT,ETC',
        pageTitle: '중요관리점 일지',
        ccpId: typeof route.params.ccpId === 'string' ? route.params.ccpId : '',
      }),
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.ccp-log',
        permission: 'mes:haccp-sheet:read',
      },
    },
    // 선행일지(서식 엔진) — 주기별 메뉴 분리
    { path: 'mes/haccp/logs/prerequisite', redirect: '/mes/haccp/logs/prerequisite/daily' },
    ...(
      [
        ['daily', 'DAILY', '일별 선행일지'],
        ['weekly', 'WEEKLY', '주별 선행일지'],
        ['monthly', 'MONTHLY', '월별 선행일지'],
        ['events', 'ON_EVENT', '수시 일지'],
      ] as const
    ).map(([slug, frequency, label]) => ({
      path: `mes/haccp/logs/prerequisite/${slug}`,
      name: `haccp.prerequisite.${slug}`,
      component: HaccpSheetListPage,
      props: { category: 'PREREQUISITE,EVENT,ETC', pageTitle: label, frequency },
      meta: {
        requiresAuth: true,
        menuCode: `haccp.prerequisite.${slug}`,
        permission: 'mes:haccp-sheet:read',
      },
    })),
    // 일지 시트 작성/조회 (Drawer 와 같은 패널)
    {
      path: 'mes/haccp/sheets/:id',
      name: 'haccp.sheet',
      component: HaccpSheetPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.ccp-log',
        permission: 'mes:haccp-sheet:read',
      },
    },
    // 선행요건 — 생산(작업)일지: 일지 엔진 생산일지(재고 원장 자동 집계 표) 달력 보드 (L.MES.GUPO.55)
    {
      path: 'mes/haccp/prerequisite/production-log',
      name: 'haccp.prerequisite.production-log',
      component: HaccpSheetListPage,
      props: { category: 'PRODUCTION', pageTitle: '생산(작업)일지', frequency: 'DAILY' },
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.production-log',
        permission: 'mes:haccp-sheet:read',
      },
    },
    // 선행요건 — 원료수불부 (입출고 집계)
    {
      path: 'mes/haccp/prerequisite/material-ledger',
      name: 'haccp.prerequisite.material-ledger',
      component: HaccpMaterialLedgerPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.material-ledger',
        permission: 'mes:inventory:read',
      },
    },
    // 선행요건 — 제품거래기록서 (판매출고 집계)
    {
      path: 'mes/haccp/prerequisite/product-ledger',
      name: 'haccp.prerequisite.product-ledger',
      component: HaccpProductLedgerPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.prerequisite.product-ledger',
        permission: 'mes:inventory:read',
      },
    },
    // 마스터 — 중요관리점(CCP) 관리: 정의·한계기준·센서 연결·이탈 알람 (설계 mes-haccp-ccp-journal-forms §6)
    {
      path: 'mes/haccp/master/ccp',
      name: 'haccp.master.ccp',
      component: CcpPointsPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.master.ccp',
        permission: 'mes:process:read',
      },
    },
    // CCP 모니터링 — 한계기준별 현재값·한계·상태·당일 트렌드·미확인 알람 (R1 에서 기준 화면에서 뺀 자리)
    {
      path: 'mes/haccp/ccp/monitoring',
      name: 'haccp.ccp-monitoring',
      component: CcpMonitoringPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.ccp-monitoring',
        permission: 'mes:process:read',
      },
    },
    // 마스터 — 일지 서식 디자이너 (엔진)
    {
      path: 'mes/haccp/master/log-templates',
      name: 'haccp.master.log-templates',
      component: HaccpTemplateDesignerPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.master.log-templates',
        permission: 'mes:haccp-template:read',
      },
    },
    // 마스터 — (구) 점검표 서식 관리
    {
      path: 'mes/haccp/master/log-forms',
      name: 'haccp.master.log-forms',
      component: HygieneCheckFormPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.master.log-forms',
        permission: 'mes:haccp-form:read',
      },
    },
    // 보건증관리 — 만료임박(D-30)/만료 대장 (jyf 라인 포팅, L.MES.GUPO.8 §4.2.8)
    {
      path: 'mes/haccp/health-certs',
      name: 'haccp.health-certs',
      component: HealthCertPage,
      meta: {
        requiresAuth: true,
        menuCode: 'haccp.health-certs',
        permission: 'mes:haccp-health-cert:read',
      },
    },
  ];

  const allMesRoutes: RouteRecordRaw[] = [
    ...masterRoutes,
    ...salesRoutes,
    ...accountingRoutes,
    ...productionRoutes,
    ...qualityRoutes,
    ...inventoryRoutes,
    ...haccpRoutes,
  ].flatMap((r) => ('component' in r ? [r] : [toResourceRoute(r as MesResourceRoute)]));

  const mesDataStatusRoutes = buildMesDataStatusRoutes(profileId);

  replaceAdminDashboardRoute(
    coreRoutes,
    {
      component: MesDashboardPage,
      meta: {
        requiresAuth: true,
        menuCode: 'dashboard',
        permission: 'mes:dashboard:read',
      },
    },
    '[mes-fe]',
  );
  appendAdminChildRoutes(coreRoutes, [...mesDataStatusRoutes, ...allMesRoutes], '[mes-fe]');
}

export interface CreateMesFeAppOptions {
  /**
   * 반죽지시(시간 지시형 작업지시) 사용 여부 — 기본 false. 구포국수 전용 기능이라
   * 켠 테넌트에만 전용 화면·POP·판정이 살아난다 (설계 mes-mixing-orders-gupo-isolation.md P0).
   */
  mixingOrders?: boolean;
  /** Tier 3/4 에서 제품명·타이틀 등만 덮어쓸 때 사용 */
  branding?: Partial<BrandingConfig>;
  mode?: 'saas' | 'on-prem';
  /** on-prem 로그인 시 숨길 테넌트 코드의 서버 값 — 미지정 시 env/VITE_AX_DEFAULT_TENANT_CODE → platform */
  defaultLoginTenantCode?: string;
  /** on-prem 에서 `/login/{code}` 로 들어갈 수 있는 추가 테넌트 (예: 테스트 테넌트 `['test']`) */
  extraLoginTenantCodes?: string[];
  /**
   * Tier 3/4 전용 라우트. `createAxApp` 의 `routes` 로 전달되어 코어 라우트 배열 뒤에 합쳐진다.
   * 동일 `path`/`name` 이 이미 MES 표준에 있으면 충돌할 수 있으므로 고객 전용 경로·이름을 쓴다.
   */
  extraRoutes?: RouteRecordRaw[];
  /**
   * 데이터현황 4종 공정 화면의 processTypeCode·차트/KPI 메타.
   * Tier 3 EGM(`kimchi`) vs Tier 4 JYBD(`jayeon-bada`) 시드에 맞게 분기한다.
   */
  processDataStatusProfile?: ProcessDataStatusProfileId;
  /**
   * 우측 AI Agent 패널·사이드바 dock 버튼 노출 여부 (기본 true).
   * Agent 미사용 고객사(Tier 4, 예: gupo)는 false 로 dock 진입점 자체를 제거한다.
   */
  agentDock?: boolean;
  /**
   * 사이드바 워크스페이스 스코프 설정 부분 오버라이드(기본 MES/HACCP/SYSTEM 위에 merge).
   * 예: 폴리켐 2앱 구성 — 같은 BE 를 AI 앱과 공유하므로 `enforceForAll: true` 로
   * 역할·토글 노출과 무관하게 스코프 필터를 강제해 AI 소속(scopeKeys ai) 루트를 숨긴다.
   */
  sidebarMenuScopeOverrides?: Partial<
    NonNullable<Parameters<typeof createAxApp>[0]['sidebarMenuScope']>
  >;
}

/**
 * MES Tier 2 동일 라우트·레이아웃으로 Vue 앱을 생성한다.
 * Tier 3 `@axsolutionsdev/mes-egm-fe` 등은 `branding`·`extraRoutes` 등으로 차별화한다.
 */
export function createMesFeApp(options?: CreateMesFeAppOptions): AxAppContext {
  // 라우트 등록보다 먼저 확정해야 한다 — 기능 라우트가 플래그를 읽는다.
  setMesFeatureFlags({ mixingOrders: options?.mixingOrders === true });
  // 모든 그리드 푸터 좌측에 공통 검색 (구포 #65) — 화면별 상단 검색바 없이도 그리드 내용을 거른다
  setAxDataGridDefaults({ quickSearch: true });
  // 기간 선택 달력에서 지금 기간과 같은 프리셋 강조 — 프리셋 픽커 전체에 한 번에 (2026-10-12)
  installDateRangeShortcutHighlight();
  ensureMesFeRoutesInstalled(options?.processDataStatusProfile ?? 'jayeon-bada');
  const envRaw = import.meta.env.VITE_DEPLOYMENT_MODE;
  const envMode: 'saas' | 'on-prem' | undefined =
    envRaw === 'saas' || envRaw === 'on-prem' ? envRaw : undefined;
  const mode: 'saas' | 'on-prem' = options?.mode ?? envMode ?? 'saas';
  const branding = mergeMesBranding(options?.branding);
  const context = createAxApp({
    mode,
    branding,
    defaultColorPreset: AX_TIER_DEFAULT_COLOR_PRESET.mes,
    routes: [...mesReportPrintRoutes, ...mesPopRoutes(), ...(options?.extraRoutes ?? [])],
    sidebarPinnedMenuGroupCodes: ['mes.pop'],
    sidebarPinnedMenuLauncherLabel: 'POP/모니터링',
    ...(options?.agentDock === false
      ? {}
      : { sidebarDockExtra: AgentDockButton, adminLayoutOverlay: AgentPanel }),
    // MES 워크스페이스 토글 — MES / HACCP / SYSTEM 3분할 ('전체' 없음).
    // - 데이터현황(mes.data-status, =CCP 일지)은 MES 영역(스마트해썹 아님).
    // - HACCP 영역은 haccp.* 만. 스마트해썹 미사용 테넌트는 서버가 haccp.* 를 숨겨
    //   'HACCP' 버튼도 사라진다(hasScopeMenus/usableScopeOptions).
    // - rootCodes 는 베이스 카탈로그(system)와 EGM/JYBD 슬림 카탈로그(jybd.*) 양쪽 코드를 포함.
    // storageKey 분리로 ERP 와 localStorage 충돌 없음.
    sidebarMenuScope: {
      storageKey: 'ax-sidebar-menu-scope-mes',
      showAllOption: false,
      options: [
        {
          value: 'mes',
          label: 'MES',
          short: 'MES',
          rootCodes: [
            'dashboard',
            'mes.master',
            'mes.sales',
            'mes.accounting',
            'mes.production',
            'mes.quality',
            'mes.inventory',
            'mes.data-status',
            'mes.pop',
          ],
        },
        {
          // rootCodes 에 'dashboard'(항상 존재) 를 넣지 않는다 — 넣으면 스마트해썹 OFF 로
          // haccp.* 가 모두 숨겨져도 HACCP 옵션이 'usable' 로 남아 버튼이 사라지지 않는다.
          // haccp.* 루트만 두어, 스마트해썹 OFF 테넌트는 HACCP 버튼 자체가 제거되도록 한다.
          value: 'haccp',
          label: 'HACCP',
          short: 'HACCP',
          rootCodes: ['haccp.dashboard', 'haccp.prerequisite', 'haccp.master'],
        },
        {
          value: 'system',
          label: 'SYSTEM',
          short: 'SYS',
          rootCodes: ['dashboard', 'system', 'jybd.users-root', 'jybd.system-root'],
        },
      ],
      adminRoles: ['ADMIN', 'SUPER_ADMIN'],
      ...options?.sidebarMenuScopeOverrides,
    },
    ...(options?.defaultLoginTenantCode !== undefined
      ? { defaultLoginTenantCode: options.defaultLoginTenantCode }
      : {}),
    ...(options?.extraLoginTenantCodes !== undefined
      ? { extraLoginTenantCodes: options.extraLoginTenantCodes }
      : {}),
  });
  installMesMenuCtrlClickNewTab(context.router);
  return context;
}
